<script lang="ts" setup>
    import '../../../css/choice.css';
    import * as fileUtils from '../../lib/FileUtils'
    import {popAlert,ModalStyles} from '../../lib/Notification'; 
    import {onMounted,ref,reactive} from 'vue';
    import {hideWindowObject} from '../../lib/Models/CommonItemsModel'; 
    import MessageSection from "../../components/Messages/MessageSection.vue";  
    import MessageBadge from "../../components/Messages/MessageBadge.vue";
    import Icon from '../../components/Icon.vue';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import {ExportStatus,IExportDataRequestData,IExportDataInput,IDownLoadInput,IExportDataRequestDataMessageBadgeItem,ITableSearchResponse} from '../../lib/Models/ExportDataRequestModel'; 
    import {ErrorReturnMessage,getExportRecords,deleteExportRequest,downloadExportRequestURL,userDownLoad} from '../../lib/Api/ExportDataRequest'; 
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import CheckboxSwitch from '../../components/Inputs/CheckboxSwitch.vue'; 
    import ChoiceTable from "../../components/ChoiceTable.vue"; 
    import {ICol}  from "../../lib/ChoiceTableHelpers" ;  
    import { ButtonDisplaySize, ButtonDisplayStyle, IconTypes, DisplayStyle, HoverCursorTypes,  MessageSectionType} from "../../Enums/UiEnums" 
    import {ActionButtonStyle} from '../../lib/Types/Types';   
    import { fullUrl } from '../../lib/ChoiceApi';
 
    const downLoadMessage = ["When exporting save file in download folder.","When exporting save file in user selected folder."];
    const messageBadgeConfiguration: Record<ExportStatus, IExportDataRequestDataMessageBadgeItem> =
    {
        [ExportStatus.Processing]: { tooltip: "The export is processing.", overrideStyle: "statusBlue", display: "In Process"},
        [ExportStatus.Expired]: { tooltip: "The export expired.", overrideStyle: "statusYellow", display: "Expired"},
        [ExportStatus.Error]: { tooltip: "The export has errored.", overrideStyle: "statusRed", display: "Error"},
        [ExportStatus.Completed]: { tooltip: "The export is ready to download.", overrideStyle: "statusGreen", display: "Completed"}
    };
    const exportDataRequestTable = ref<InstanceType<typeof ChoiceTable>>(); 
    const exportDataRequestColumns = ref<ICol[]>([ 
        { Name: 'ID', DataField: 'exportDataRequestID'}, 
        { Name: 'Type', DataField: 'exportTypeDescription' }, 
        { Name: 'Description', DataField: 'exportDescription' }, 
        { Name: 'Expiration', DataField: 'expirationTime' }, 
        { Name: 'Created', DataField: 'postDate' }, 
        { Name: 'Message', DataField: 'userMessage' }, 
        { Name: 'Status', DataField: 'exportStatus' },
        { Name: 'Download Attempts', DataField: 'downLoadCount' },
        { Name: 'Action', DataField: 'empty' }
    ]); 
    const formData = reactive <
    {      
        exportDataRequestData?: IExportDataRequestData[] ,
        ExportLocation: boolean
    }>({       
        ExportLocation: false
    });

    onMounted(async () => { 
        hideWindowObject("LoadingMessage");
        await getReportData();
    });  
    
    async function getReportData() : Promise<void> { 
        const returnResults: IExportDataRequestData[] = await getExportRecords(); 
        if (returnResults) {
            formData.exportDataRequestData = returnResults;
            return;
        }
        formData.exportDataRequestData = [];
    } 
    async function deleteReportData(export_request_id: number) : Promise<void> { 
        const data: IExportDataInput = { ExportDataRequestID: export_request_id};
        const returnResults = await deleteExportRequest(data); 
        const messageTitle = "Remove Export Data Request";
        if (returnResults.error) {  
            await popAlert(returnResults.error?.error ?? ErrorReturnMessage.delete,messageTitle,ModalStyles.danger);
            return;
        }         
        if (returnResults.success?.errorCode !== 0) {  
            await popAlert(returnResults.success?.errorMessage ?? ErrorReturnMessage.delete,messageTitle,ModalStyles.danger);
            return;
        } 
        // Refresh the report data
        await getReportData();
    } 

    function GetMessageBadgeTitle(statusId: ExportStatus) {
        const config = messageBadgeConfiguration[statusId];
        if (config == undefined)
            return "Uknown";
        return config.tooltip;
    }
    function GetMessageBadgeOverrideStyle(statusId: ExportStatus) {
        const config = messageBadgeConfiguration[statusId];
        if (config == undefined)
            return "";
        return config.overrideStyle;
    }
    function GetMessageBadgeOverrideDisplay(statusId: ExportStatus) {
        const config = messageBadgeConfiguration[statusId];
        if (config == undefined)
            return "Unknown";
        return config.display;
    }

    async function onDownload(row: IExportDataRequestData) { 
        if ((formData.exportDataRequestData?.length ?? 0) == 0) {
            return;
        }
        const search_row = formData.exportDataRequestData?.filter(item => item.exportDataRequestID == row.exportDataRequestID);
        if (search_row) {
            const data: IDownLoadInput = { ExportDataRequestID: row.exportDataRequestID, SheetName: search_row[0].fileName}; 
            if (formData.ExportLocation) {
                const user_download_results : ITableSearchResponse = await userDownLoad(data);
                await fileUtils.saveBase64ToFileWithPicker(user_download_results.responseMessage, search_row[0].fileName);
            }
            else {
                await fileUtils.downloadResourceAsync(fullUrl(downloadExportRequestURL(row.exportDataRequestID,search_row[0].fileName)),search_row[0].fileName);
            }
            // Refresh the report data
            await getReportData();
        }
    } 
    async function onRemove(row: IExportDataRequestData) {
        await deleteReportData(row.exportDataRequestID); 
    } 
</script> 
 
<style scoped> 
    .downloadLinkBlue {
        color: #6495ed;
    }
    .statusYellow {
        background-color: #fff3cd;
        color: black;
    }
    .statusGreen {
        background-color: #d1e7dd;
        color: black;
    }
    .statusBlue {
        background-color: #cff4fc;
        color: black;
    } 
    .statusRed {
        background-color: #f8d7da;
        color: black;
    }
</style>

<template>       
    <WindowHeaderFooter
    :headerHeight="9.5"
    :footerHeight="3.5">
        <template v-slot:WindowHeader> 
            <div class="isalus-page-title-large pt-2 pb-2">Export Data Requests</div>

            <div class="label-isalus-medium px-2"> 
                Export Data Requests allows you to export various datasets from the system.
                <br/>
                When you make an Export Request, a Toast message will appear on the main console, notifying you that the dataset is being prepared. Clicking on the message will direct you to this screen.
                <br/><br/>
                Please note: <b>Each file will only be available for a short period of time - which varies by data set type, then it will be removed in a nightly cleanup process.</b>
            </div>
        </template>
        <template v-slot:WindowBody>
            <p v-if="(formData.exportDataRequestData?.length ?? 0) == 0"  class="pt-2">
                <MessageSection 
                    :type="MessageSectionType.Row" 
                    :displayStyle="DisplayStyle.Info" 
                    :showIcon="false">
                    There are no export data requests to display.
                </MessageSection>
            </p>
            <p v-if="(formData.exportDataRequestData?.length ?? 0) > 0"  class="pt-2">
                <ChoiceTable 
                    ref="exportDataRequestTable"
                    :columns="exportDataRequestColumns" 
                    :data="formData.exportDataRequestData" 
                    :show-filters="false" 
                    :show-footer="false" 
                    :allowSorting="true"
                    fontSize="smaller"
                    :key-value-data-member="'exportDataRequestID'"> 
                    <template #cell="{col, row, value}">
                        <template v-if="'Status' === col.Name">
                            <MessageBadge 
                                :title="GetMessageBadgeTitle(row.exportStatus)" 
                                :overrideStyle="GetMessageBadgeOverrideStyle(row.exportStatus)">
                                {{ GetMessageBadgeOverrideDisplay(row.exportStatus) }}
                            </MessageBadge>
                        </template>
                        <template v-if="'Action' === col.Name">
                   
                            <template v-if="row.exportStatus == ExportStatus.Completed"> 
                                <Icon :icon="IconTypes.Export" class="downloadLinkBlue"  :hoverCursor="HoverCursorTypes.Pointer" title="Download the excel file." @click="onDownload(row as IExportDataRequestData)"/>&nbsp;&nbsp;&nbsp;
                            </template>   
                            <template v-if="row.exportStatus != ExportStatus.Processing">
                                <Icon :icon="IconTypes.Trashcan" :displayStyle="DisplayStyle.Danger" :hoverCursor="HoverCursorTypes.Pointer" title="Remove this export request." @click="onRemove(row as IExportDataRequestData)"/>&nbsp;&nbsp;&nbsp;
                            </template>                        
                        </template>
                    </template>
                </ChoiceTable>
            </p> 
        </template>
        <template v-slot:WindowFooter> 
            <div class="row">
                <div class="col-9">
                    <CheckboxSwitch
                        :disabled="false"
                        :defaultMessage="downLoadMessage[0]"
                        :switchedMessage="downLoadMessage[1]"
                        v-model="formData.ExportLocation" 
                    />
                </div>
                <div class="col-3 text-end">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Refresh"
                        :text="'Refresh'"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="getReportData"/>
                </div>
               
            </div> 
        </template>
    </WindowHeaderFooter> 
</template>
